import "./JTBDSelection.scss";

import {
  Breadcrumb,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  ChevronEndIcon,
  ChevronStartIcon,
  Flex,
  Loader,
  Text,
  Tooltip,
  VideoCameraEmphasisIcon,
} from "@fluentui/react-northstar";
import { TeamsFeatureFlags, TeamsSurveyTypes } from "@teams/types";
import React, { Fragment, MouseEvent, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";

import { useUserInfo } from "../../_shared/actions/useUserInfo";
import HotTakesIcon from "../../_shared/assets/pollytypes/hot-takes.svg";
import IcebreakersIcon from "../../_shared/assets/pollytypes/icebreakers.svg";
import QandAIcon from "../../_shared/assets/pollytypes/qanda.svg";
import QuizIcon from "../../_shared/assets/pollytypes/quiz.svg";
import SurveyIcon from "../../_shared/assets/pollytypes/survey.svg";
import TriviaIcon from "../../_shared/assets/pollytypes/trivia.svg";
import { ResponseUsageMeter } from "../../_shared/components/MeterBars/ResponseUsageMeter";
import { PollyIcon, PollyIconName } from "../../_shared/components/PollyIcon";
import { useTeamsContext } from "../../_shared/state/TeamsContextProvider";
import { isMobileClient } from "../../_shared/utils/clientHelper";
import { clientEventTracker } from "../../_shared/utils/clientTelemetry";
import { getAuthoringPath } from "../../_shared/utils/getAuthoringPath";
import { useTemplates } from "../actions/useTemplates";
import { CardList } from "./components/CardList";

type Props = {
  onRouteSelected?: (route: string, width?: number) => void;
  hostPage: string;
  hideResponseMeter?: boolean;
};

export const JTBDSelection: React.FC<Props> = ({
  onRouteSelected,
  hostPage,
  hideResponseMeter,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useUserInfo();
  const { teamsContext } = useTeamsContext();
  const [items, setItems] = useState<JTBDCategoryItem[]>([HomeItem]);
  const { loadTemplates, isTemplatesLoading, templates } = useTemplates();

  useEffect(() => {
    loadTemplates(teamsContext, "global");

    clientEventTracker.track("jtbd viewed", user, {
      client: teamsContext.app.host.clientType,
      hostPage,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateToRoute = (route: string, width?: number) => {
    if (onRouteSelected) {
      onRouteSelected(route, width);
    } else {
      history.push(route);
    }
  };

  const isMobile = isMobileClient(teamsContext);

  const [currentItem, currentItemChildren] = useMemo(() => {
    const ci = items[items.length - 1];

    let subItems = ci.items!;

    if (!isMobile) {
      while (subItems.length % 3 !== 0) {
        //Add empty items to fill the row
        const emptyItem: JTBDEmptyItem = {
          type: "empty",
          id: `empty-${ci.name}-${subItems.length}`,
        };
        subItems.push(emptyItem);
      }
    }

    return [ci, subItems];
  }, [items, isMobile]);

  return (
    <div className="jtbd">
      <Flex className="jtbd-selection" hAlign="center">
        <Flex
          column
          gap="gap.large"
          className={`jtbd-selection-container ${
            isMobile ? "" : "jtbd-selection-container-desktop"
          }`}
        >
          <Flex className="jtbd-selection-info-bar">
            <Flex.Item grow>
              <div className="polly-authoring-page__back-button">
                {(items.length > 1 || items[0] !== HomeItem) && (
                  <Button
                    text
                    content={
                      <Flex vAlign="center">
                        <ChevronStartIcon xSpacing="after" />
                        Back
                      </Flex>
                    }
                    onClick={() => {
                      if (items.length > 1) {
                        setItems(items.slice(0, items.length - 1));
                      } else {
                        setItems([HomeItem]);
                      }
                    }}
                  />
                )}
              </div>
            </Flex.Item>
            {!hideResponseMeter && (
              <Flex.Item push>
                <ResponseUsageMeter />
              </Flex.Item>
            )}
          </Flex>

          {isTemplatesLoading && <Loader size="larger" />}

          {!isTemplatesLoading && (
            <>
              <Text
                size="larger"
                weight="bold"
                content={currentItem.itemsTitle}
                onDoubleClick={(e: MouseEvent) => {
                  if (e.shiftKey) {
                    console.info(teamsContext);
                    const value = JSON.stringify(teamsContext, null, 2);
                    alert(value);
                  }
                }}
              />

              <Flex column gap="gap.small" className="jtbd-selection-w-100">
                <Breadcrumb>
                  {items.map((item, index) => (
                    <Fragment key={item.name}>
                      {index > 0 && (
                        <BreadcrumbDivider>
                          <ChevronEndIcon size="small" />
                        </BreadcrumbDivider>
                      )}
                      <BreadcrumbItem
                        onClick={
                          index < items.length - 1
                            ? () => setItems([...items.slice(0, index + 1)])
                            : undefined
                        }
                        style={{ cursor: index < items.length - 1 ? "pointer" : undefined }}
                      >
                        <Flex vAlign="center" gap="gap.smaller">
                          {item.icon && <PollyIcon icon={item.icon} inheritColor />}
                          <Text content={item.name} />
                        </Flex>
                      </BreadcrumbItem>
                    </Fragment>
                  ))}
                </Breadcrumb>

                <CardList
                  alignItems="center"
                  items={currentItemChildren
                    .filter(item => {
                      if (
                        item.type === "category" &&
                        item?.meetingOnly &&
                        !teamsContext?.meeting?.id
                      ) {
                        return false;
                      }
                      if (
                        item.type === "polly-type" &&
                        item.featureFlag &&
                        !user.featureFlags[item.featureFlag]
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map((item: JTBDItem | JTBDCategoryItem) => {
                      let body: JSX.Element | undefined;
                      let footer: JSX.Element | undefined;
                      const classes = [];

                      switch (item.type) {
                        case "template":
                          const template = templates.find(t => t.id === item.id);

                          if (template) {
                            body = (
                              <Flex column gap="gap.small">
                                <Text content={template.templateName} weight="bold" />
                                <Text content={template.templateDescription} size="small" />
                              </Flex>
                            );
                          }
                          break;
                        case "polly-type":
                          body = (
                            <Flex
                              column={isMobile ? false : true}
                              hAlign="center"
                              gap={isMobile ? "gap.large" : "gap.smaller"}
                            >
                              <Flex column>
                                {item.icon && (
                                  <img
                                    src={item.icon}
                                    alt={item.name}
                                    className="hover-brand"
                                    height={30}
                                  />
                                )}
                                <Text
                                  content={item.name}
                                  weight="bold"
                                  size="large"
                                  align="center"
                                />
                              </Flex>

                              <Flex column vAlign="end" hAlign="center">
                                {item.meetingOnly && !teamsContext?.meeting?.id && (
                                  <Tooltip
                                    trigger={
                                      <Flex
                                        hAlign="center"
                                        vAlign="center"
                                        className="jtbd-selection-meeting-only-warning"
                                      >
                                        <VideoCameraEmphasisIcon size="small" xSpacing="after" />
                                        <Text content="Only in meetings" size="small" />
                                      </Flex>
                                    }
                                    content="Only available in Microsoft Teams Meetings.  Add Polly app to your meeting or select 'Learn More' below to get help"
                                  />
                                )}

                                {item.helpLink && (
                                  <a
                                    href={item.helpLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="brand-link hover-underline"
                                  >
                                    <Text content="Learn More" weight="bold" />
                                  </a>
                                )}
                              </Flex>
                            </Flex>
                          );

                          if (item.meetingOnly && !teamsContext?.meeting?.id) {
                            classes.push("jtbd-selection-meeting-only");
                          }
                          break;
                        case "category":
                          body = (
                            <Flex gap="gap.smaller" vAlign="center" column hAlign="center">
                              {item.icon && (
                                <PollyIcon
                                  icon={item.icon}
                                  size="xl"
                                  className="hover-brand"
                                  inheritColor
                                />
                              )}
                              <Text content={item.name} weight="bold" size="large" align="center" />
                            </Flex>
                          );
                          break;
                        case "create-new":
                          body = (
                            <Flex column gap="gap.small" hAlign="center">
                              <Text
                                content="Not finding what you're looking for?"
                                size="small"
                                weight="light"
                                align="center"
                              />

                              <Flex
                                hAlign="center"
                                vAlign="center"
                                gap="gap.smaller"
                                className="hover-brand"
                              >
                                <PollyIcon icon="add-choice-icon" size="small" inheritColor />
                                <Text content="Create New" weight="bold" />
                              </Flex>
                            </Flex>
                          );
                          classes.push("jtbd-selection-create-new");
                          break;
                        case "link":
                          body = (
                            <Flex column gap="gap.small" hAlign="center">
                              <Text content={item.content} weight="bold" />

                              <Text content={item.description} size="small" />
                            </Flex>
                          );
                          break;
                      }

                      return {
                        key: item.id,
                        body,
                        footer,
                        className: classes.join(" "),
                        onClicked: () => {
                          const category = items.map(item => item.name).join(",");

                          let eventProperties: Record<string, string> | undefined;
                          let navTo: string | undefined;

                          switch (item.type) {
                            case "template":
                              const template = templates.find(t => t.id === item.id);

                              if (!template) {
                                return;
                              }

                              navTo = getAuthoringPath(template.type, location, {
                                templateId: template.id,
                                trackingSource: "jtbd-template",
                              });

                              eventProperties = {
                                templateId: item.id,
                                templateName: template.templateName,
                              };
                              break;
                            case "polly-type":
                              if (item.meetingOnly && !teamsContext?.meeting?.id) {
                                return;
                              }

                              navTo = getAuthoringPath(item.pollyType, location, {
                                trackingSource: "jtbd-polly-type",
                              });

                              eventProperties = {
                                pollyType: item.pollyType,
                              };

                              break;
                            case "category":
                              eventProperties = {
                                categoryId: item.id,
                                categoryName: item.name,
                              };
                              setItems([...items, item]);
                              break;
                            case "create-new":
                              setItems([PollyTypeItem]);
                              break;
                            case "link":
                              eventProperties = {
                                linkId: item.id,
                                href: item.href,
                              };

                              window.open(item.href, item.target);
                              break;
                          }

                          clientEventTracker.track("jtbd item selected", user, {
                            type: item.type,
                            category,
                            hostPage,
                            ...eventProperties,
                          });

                          if (navTo) {
                            navigateToRoute(navTo);
                          }
                        },
                      };
                    })}
                />
              </Flex>

              {items.length === 1 && items[0] === HomeItem && (
                <>
                  <Text content="Or" align="center" size="large" weight="semilight" />

                  <Flex gap="gap.medium">
                    <Button
                      fluid
                      className="jtbd-selection-xl-button"
                      content="Create New"
                      icon={<PollyIcon icon="add-choice-icon" inheritColor />}
                      onClick={() => {
                        clientEventTracker.track("jtbd create new clicked", user, {
                          category: items.map(i => i.name).join(","),
                          hostPage,
                        });

                        // Show the question type menu for meetings
                        // otherwise navigate to the form
                        if (teamsContext?.meeting?.id) {
                          setItems([PollyTypeItem]);
                        } else {
                          const navTo = getAuthoringPath("poll", location, {
                            trackingSource: "jtbd-create-new",
                          });

                          navigateToRoute(navTo);
                        }
                      }}
                    />
                    <Button
                      fluid
                      className="jtbd-selection-xl-button"
                      content="All templates"
                      icon={<PollyIcon icon="templates_2" inheritColor />}
                      onClick={() => {
                        clientEventTracker.track("jtbd all templates clicked", user, {
                          category: items.map(i => i.name).join(","),
                          hostPage,
                        });

                        navigateToRoute(`/msteams/content/templates${location.search}`, 1400);
                      }}
                    />
                  </Flex>
                </>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

const CreateNewItem: JTBDCreateNewItem = {
  type: "create-new",
  id: "create-new",
};

const QNAPollyItem: JTBDPollyTypeItem = {
  name: "Q&A",
  id: "qna",
  type: "polly-type",
  pollyType: "qna",
  meetingOnly: true,
  helpLink:
    "https://www.polly.ai/help/microsoft-teams/qa-in-teams?utm_source=teams&utm_medium=app&utm_campaign=inapp-message&utm_content=jtbd",
};

const IcebreakerPollItem: JTBDPollyTypeItem = {
  name: "Icebreaker",
  id: "icebreaker",
  type: "polly-type",
  pollyType: "icebreaker",
  meetingOnly: true,
  featureFlag: "icebreaker",
};

const HotTakePollItem: JTBDPollyTypeItem = {
  name: "Hot Take",
  id: "hot-take",
  type: "polly-type",
  pollyType: "hot-take",
  meetingOnly: true,
  featureFlag: "hotTake",
};

const TriviaPollyItem: JTBDPollyTypeItem = {
  name: "Trivia",
  id: "trivia",
  type: "polly-type",
  pollyType: "trivia",
  meetingOnly: true,
  helpLink:
    "https://www.polly.ai/help/microsoft-teams/trivia-guide?utm_source=teams&utm_medium=app&utm_campaign=inapp-message&utm_content=jtbd",
};

const QuizPollyItem: JTBDPollyTypeItem = {
  name: "Quiz",
  id: "quiz",
  type: "polly-type",
  pollyType: "quiz",
  meetingOnly: false,
  helpLink:
    "https://www.polly.ai/help/microsoft-teams/quizzes-in-live-meetings?utm_source=teams&utm_medium=app&utm_campaign=inapp-message&utm_content=jtbd",
};

const HomeChoiceItems: JTBDCategoryItem[] = [
  {
    name: "Meeting Attendees",
    id: "meeting-attendees",
    type: "category",
    icon: "meetings",
    itemsTitle: "What is your goal?",
    items: [
      {
        name: "Get Input Before Meeting",
        id: "meeting-prep",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:ebc40178-532a-42df-812f-8326c95c4706" },
          { type: "template", id: "qut:85335390-d8eb-4269-9be2-33620547e429" },
          CreateNewItem,
        ],
      },
      {
        name: "Get Input During Meeting",
        id: "feedback",
        type: "category",
        itemsTitle: "Choose a polly to create",
        meetingOnly: true,
        items: [
          IcebreakerPollItem,
          HotTakePollItem,
          QNAPollyItem,
          { type: "template", id: "qut:f45adf94-9f02-4ec0-be91-6259fb048029" },
          { type: "template", id: "qut:7686c621-14fe-4fbe-9514-1de4564a4314" },
          CreateNewItem,
        ],
      },
      {
        name: "Get Feedback After Meeting",
        id: "after-meeting",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:b8fd19cc-6d27-491d-a476-f88a91837edc" },
          { type: "template", id: "qut:2403cf5a-c81b-421a-b728-67647e67513e" },
          { type: "template", id: "qut:f45adf94-9f02-4ec0-be91-6259fb048029" },
          { type: "template", id: "qut:7686c621-14fe-4fbe-9514-1de4564a4314" },
          CreateNewItem,
        ],
      },
      {
        name: "Have Fun!",
        id: "have-fun",
        type: "category",
        itemsTitle: "Choose a polly to create",
        meetingOnly: true,
        items: [TriviaPollyItem],
      },
    ],
  },
  {
    name: "Coworkers or Employees",
    id: "coworkers",
    type: "category",
    icon: "team",
    itemsTitle: "What is your goal?",
    items: [
      {
        name: "Collect General Feedback",
        id: "feedback",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:f45adf94-9f02-4ec0-be91-6259fb048029" },
          { type: "template", id: "qut:c808a82a-f21a-4e51-822b-78ee7904691b" },
          { type: "template", id: "qut:7686c621-14fe-4fbe-9514-1de4564a4314" },
          { type: "template", id: "qut:c7e69a81-f8c6-4d36-970e-e9da0a58284b" },
          { type: "template", id: "qut:7db0bf95-5973-4be1-8930-c62ef3a5a884" },
          CreateNewItem,
        ],
      },
      {
        name: "Measure Satisfaction and Happiness",
        id: "measure-satisfaction",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:8d1208a5-c12d-4ba4-a7e2-d6ddc1b2544c" },
          { type: "template", id: "qut:e9e39a52-9a43-46ca-b43f-e3138f5bfee6" },
          { type: "template", id: "qut:319d2beb-1ef6-4294-b050-18f67593eed2" },
          { type: "template", id: "qut:7b7a9bf4-3e9e-49ae-acd7-c76d7f0b5b8e" },
          CreateNewItem,
        ],
      },
      {
        name: "Hiring and Onboarding",
        id: "hiring-onboarding",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:725fd957-431c-4541-8259-cbb4e6fe6e36" },
          { type: "template", id: "qut:e1b9ec69-8284-4f9e-8d3b-64758717eef7" },
          { type: "template", id: "qut:c24ace69-9af1-4d6a-8832-ee8ff5751e40" },
          { type: "template", id: "qut:8f92405c-0996-4767-b09c-fb0edc0cb79d" },
          { type: "template", id: "qut:f988e38e-5af5-4e0a-98f9-f1cc65ba10e4" },
          CreateNewItem,
        ],
      },
      {
        name: "Track Work Progress",
        id: "progress-work",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:20ff5ac3-2ef8-4ab8-a84d-2922791ecef2" },
          {
            id: "agile-polly",
            content: "Stand-ups with AgilePolly",
            type: "link",
            target: "_blank",
            href: "https://teams.microsoft.com/l/app/a887d3e9-d9ef-4c46-851e-f7753ef29452?source=polly-jtbd",
            description:
              "Bring automated stand ups to your team with the specialized AgilePolly app!",
          },
          CreateNewItem,
        ],
      },
      {
        name: "Evaluate Performance",
        id: "eval-performance",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:8af8d89d-1ff0-44a9-a324-50e11f6ec90d" },
          { type: "template", id: "qut:f988e38e-5af5-4e0a-98f9-f1cc65ba10e4" },
          CreateNewItem,
        ],
      },
      {
        name: "Have Fun!",
        id: "have-fun",
        type: "category",
        itemsTitle: "Choose a polly to create",
        meetingOnly: true,
        items: [TriviaPollyItem],
      },
    ],
  },
  {
    name: "Students or Trainees",
    id: "students",
    type: "category",
    icon: "education",
    itemsTitle: "What is your goal?",
    items: [
      {
        name: "Collect General Feedback",
        id: "feedback",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:f45adf94-9f02-4ec0-be91-6259fb048029" },
          { type: "template", id: "qut:7686c621-14fe-4fbe-9514-1de4564a4314" },
          QNAPollyItem,
          { type: "template", id: "qut:c808a82a-f21a-4e51-822b-78ee7904691b" },
          CreateNewItem,
        ],
      },
      {
        name: "Give a Quiz",
        id: "quiz",
        type: "category",
        itemsTitle: "Choose a quiz to create",
        items: [
          {
            name: "Quiz",
            id: "quiz",
            type: "polly-type",
            pollyType: "quiz",
            icon: QuizIcon,
          },
          CreateNewItem,
        ],
      },
      {
        name: "Measure Satisfaction and Happiness",
        id: "satisfaction",
        type: "category",
        itemsTitle: "Choose a polly to create",
        items: [
          { type: "template", id: "qut:8d1208a5-c12d-4ba4-a7e2-d6ddc1b2544c" },
          { type: "template", id: "qut:9db9aa8e-84b0-4635-8963-d9cd9779a99c" },
          { type: "template", id: "qut:aeecb1d4-2242-4ddd-928a-a48aea8a5d7b" },
          CreateNewItem,
        ],
      },
    ],
  },
];

const HomeItem: JTBDCategoryItem = {
  name: "Categories",
  id: "home",
  type: "category",
  itemsTitle: "Who is your audience?",
  items: HomeChoiceItems,
};

const PollyTypeItem: JTBDCategoryItem = {
  name: "Create New",
  id: "create-new",
  type: "category",
  itemsTitle: "Choose a polly type",
  items: [
    {
      ...IcebreakerPollItem,
      icon: IcebreakersIcon,
    },
    {
      ...HotTakePollItem,
      icon: HotTakesIcon,
    },
    {
      name: "Question",
      id: "question",
      type: "polly-type",
      pollyType: "poll",
      icon: SurveyIcon,
    },
    {
      ...QNAPollyItem,
      icon: QandAIcon,
    },
    { ...TriviaPollyItem, icon: TriviaIcon },
    { ...QuizPollyItem, icon: QuizIcon },
  ],
};

type JTBDItemBase = {
  id: string;
};

type JTBDPollyTypeItem = JTBDItemBase & {
  type: "polly-type";
  name: string;
  pollyType: TeamsSurveyTypes;
  icon?: string;
  meetingOnly?: boolean;
  helpLink?: string;
  featureFlag?: keyof TeamsFeatureFlags;
};

type JTBDTemplateItem = JTBDItemBase & {
  type: "template";
  id: `qut:${string}`;
};

type JTBDEmptyItem = JTBDItemBase & {
  type: "empty";
};

type JTBDCreateNewItem = JTBDItemBase & {
  type: "create-new";
};

type JTBDLinkItem = JTBDItemBase & {
  content: React.ReactNode;
  description: string;
  type: "link";
  target?: "_blank";
  href: string;
};

type JTBDCategoryItem = JTBDItemBase & {
  name: string;
  type: "category";
  icon?: PollyIconName;
  items: JTBDItem[];
  itemsTitle: string;
  meetingOnly?: boolean;
};

type JTBDItem =
  | JTBDPollyTypeItem
  | JTBDTemplateItem
  | JTBDEmptyItem
  | JTBDCategoryItem
  | JTBDCreateNewItem
  | JTBDLinkItem;
